import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import WhotGameTable from '../whot/components/WhotGameTable';
import WhotPlayerHand from '../whot/components/WhotPlayerHand';
import WhotControls from '../whot/components/WhotControls';
import { CARD_SHAPES, SPECIAL_CARDS } from '../whot/utils/constants';
import { createOrderedCards } from '../whot/utils/deck';
import '../styles/Whot.css';

const HOUSE_RULES = {
  whotEnabled: true,
  generalMarket14: true,
  pick2: true,
  holdOn1: true,
  pick3On5: true,
  suspension8: true,
  oneRound: false,
};

const BOT_DELAY_MS = 700;
const PLAYER_ID = 'anonymous-player';
const BOT_ID = 'anonymous-bot';

function chooseBestShape(hand = []) {
  const counts = {
    [CARD_SHAPES.CIRCLE]: 0,
    [CARD_SHAPES.SQUARE]: 0,
    [CARD_SHAPES.STAR]: 0,
    [CARD_SHAPES.TRIANGLE]: 0,
    [CARD_SHAPES.CROSS]: 0,
  };

  hand.forEach((card) => {
    if (card?.number === SPECIAL_CARDS.WHOT.number) return;
    if (counts[card?.shape] != null) counts[card.shape] += 1;
  });

  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || CARD_SHAPES.CIRCLE;
}

function shuffleCards(cards) {
  const list = [...cards];
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

function createBaseGame() {
  return {
    players: [
      { id: PLAYER_ID, name: 'You', cards: [], isBot: false },
      { id: BOT_ID, name: 'Bot', cards: [], isBot: true },
    ],
    discardPile: [],
    currentPlayerIndex: 0,
    currentShape: null,
    direction: 1,
    holdOn: { active: false, holderId: null, startup: false },
    pendingPick2: { active: false, count: 2 },
    pendingPick3: { active: false, count: 3 },
    awaitingShapeCallPlayerId: null,
    deck: shuffleCards(createOrderedCards(true)),
    deckRemaining: 0,
    gameStatus: 'in_progress',
    winner: null,
    houseRules: { ...HOUSE_RULES },
  };
}

function advancePlayerIndex(game, baseIndex, steps = 1) {
  const count = game.players.length;
  if (count <= 0) return 0;
  const raw = baseIndex + steps * game.direction;
  return ((raw % count) + count) % count;
}

function endGameByOneRound(game) {
  let bestPlayer = null;
  let lowestScore = Infinity;

  game.players.forEach((player) => {
    const score = (player.cards || []).reduce((sum, card) => {
      if (!card) return sum;
      const value = Number(card.number) || 0;
      const isStar = String(card.shape) === CARD_SHAPES.STAR;
      return sum + (isStar ? value * 2 : value);
    }, 0);
    
    if (score < lowestScore) {
      lowestScore = score;
      bestPlayer = player;
    }
  });

  if (bestPlayer) {
    game.winner = { id: bestPlayer.id, name: bestPlayer.name };
  } else if (game.players.length > 0) {
    game.winner = { id: game.players[0].id, name: game.players[0].name };
  }
  game.gameStatus = 'finished';
}

function drawOneSafe(game) {
  if (game.deck.length > 0) return game.deck.shift();
  if (game.discardPile.length <= 1) return null;

  if (game.houseRules.oneRound) {
    endGameByOneRound(game);
    return null;
  }

  const keep = game.discardPile[game.discardPile.length - 1];
  const recycle = game.discardPile.slice(0, -1);
  game.discardPile = [keep];
  game.deck = shuffleCards(recycle);
  return game.deck.shift() || null;
}

function isValidMoveWithShape(card, topCard, currentShape) {
  if (!card || !topCard) return false;
  if (card.number === SPECIAL_CARDS.WHOT.number) return true;
  if (currentShape) return card.shape === currentShape;
  if (card.number === topCard.number) return true;
  if (card.shape === topCard.shape) return true;
  return false;
}

function setupOpening(game) {
  const cardsPerPlayer = game.players.length === 2 ? 7 : 5;
  game.players.forEach((player) => {
    player.cards = [];
    for (let i = 0; i < cardsPerPlayer; i += 1) {
      const one = drawOneSafe(game);
      if (one) player.cards.push(one);
    }
  });

  let firstCard = drawOneSafe(game);
  let guard = 0;
  while (firstCard && firstCard.number === SPECIAL_CARDS.WHOT.number && guard < 25) {
    game.deck.push(firstCard);
    game.deck = shuffleCards(game.deck);
    firstCard = drawOneSafe(game);
    guard += 1;
  }

  if (firstCard) game.discardPile.push(firstCard);

  if (firstCard?.number === SPECIAL_CARDS.PICK_THREE && game.houseRules.pick3On5) {
    game.pendingPick3 = { active: true, count: 3 };
  }
  if (firstCard?.number === SPECIAL_CARDS.PICK_TWO && game.houseRules.pick2) {
    game.pendingPick2 = { active: true, count: 2 };
  }
  if (firstCard?.number === SPECIAL_CARDS.SUSPENSION && game.houseRules.suspension8) {
    game.currentPlayerIndex = advancePlayerIndex(game, game.currentPlayerIndex, 1);
  }
  if (firstCard?.number === SPECIAL_CARDS.HOLD_ON && game.houseRules.holdOn1) {
    game.holdOn = { active: true, holderId: game.players[0]?.id, startup: true };
  }
  if (firstCard?.number === SPECIAL_CARDS.GENERAL_MARKET && game.houseRules.generalMarket14) {
    game.players.forEach((player) => {
      const one = drawOneSafe(game);
      if (one) player.cards.push(one);
    });
  }

  game.deckRemaining = game.deck.length;
}

function applySpecialEffects(game, card, isCounteringPick3) {
  let nextIdx = advancePlayerIndex(game, game.currentPlayerIndex, 1);

  if (card.number === SPECIAL_CARDS.SUSPENSION && game.houseRules.suspension8) {
    nextIdx = advancePlayerIndex(game, game.currentPlayerIndex, 2);
  } else if (card.number === SPECIAL_CARDS.HOLD_ON && game.houseRules.holdOn1) {
    game.holdOn = { active: true, holderId: game.players[game.currentPlayerIndex]?.id };
    nextIdx = game.currentPlayerIndex;
  } else if (card.number === SPECIAL_CARDS.PICK_TWO && game.houseRules.pick2) {
    const victim = advancePlayerIndex(game, game.currentPlayerIndex, 1);
    game.pendingPick2 = { active: true, count: 2 };
    nextIdx = victim;
  } else if (card.number === SPECIAL_CARDS.PICK_THREE && game.houseRules.pick3On5 && !isCounteringPick3) {
    const victim = advancePlayerIndex(game, game.currentPlayerIndex, 1);
    game.pendingPick3 = { active: true, count: 3 };
    nextIdx = victim;
  } else if (card.number === SPECIAL_CARDS.PICK_THREE && isCounteringPick3) {
    game.pendingPick3 = { active: false, count: 3 };
    nextIdx = advancePlayerIndex(game, game.currentPlayerIndex, 1);
  } else if (card.number === SPECIAL_CARDS.GENERAL_MARKET && game.houseRules.generalMarket14) {
    const currentIdx = game.currentPlayerIndex;
    game.players.forEach((player, idx) => {
      if (idx === currentIdx) return;
      const one = drawOneSafe(game);
      if (one) player.cards.push(one);
    });
    nextIdx = game.currentPlayerIndex;
  }

  return nextIdx;
}

function cloneGame(game) {
  return {
    ...game,
    players: game.players.map((p) => ({ ...p, cards: [...p.cards] })),
    discardPile: [...game.discardPile],
    deck: [...game.deck],
    holdOn: { ...(game.holdOn || {}) },
    pendingPick2: { ...(game.pendingPick2 || {}) },
    pendingPick3: { ...(game.pendingPick3 || {}) },
  };
}

function createNewMatch() {
  const game = createBaseGame();
  setupOpening(game);
  return game;
}

function playCard(game, playerId, cardId) {
  if (game.gameStatus !== 'in_progress') return { ok: false, error: 'Game not in progress', game };

  const next = cloneGame(game);
  const currentPlayer = next.players[next.currentPlayerIndex];
  if (!currentPlayer || currentPlayer.id !== playerId) return { ok: false, error: 'Not your turn', game };
  if (next.awaitingShapeCallPlayerId && next.awaitingShapeCallPlayerId !== playerId) return { ok: false, error: 'Shape call pending', game };

  const cardIdx = currentPlayer.cards.findIndex((card) => card.id === cardId);
  if (cardIdx === -1) return { ok: false, error: 'Card not found', game };

  const card = currentPlayer.cards[cardIdx];
  const top = next.discardPile[next.discardPile.length - 1];
  const pendingPick3Active = Boolean(next.pendingPick3?.active && next.houseRules.pick3On5);
  const pendingPick2Active = Boolean(next.pendingPick2?.active && next.houseRules.pick2);
  const isCounteringPick3 = pendingPick3Active && card.number === SPECIAL_CARDS.PICK_THREE;

  if (pendingPick3Active && !isCounteringPick3) return { ok: false, error: 'Pick 3 active', game };
  if (pendingPick2Active) return { ok: false, error: 'Pick 2 active', game };
  if (!isCounteringPick3 && !isValidMoveWithShape(card, top, next.currentShape)) return { ok: false, error: 'Invalid move', game };

  currentPlayer.cards.splice(cardIdx, 1);
  next.discardPile.push(card);
  next.currentShape = null;

  if (currentPlayer.cards.length === 0 && card.number === SPECIAL_CARDS.WHOT.number) {
    const forced = drawOneSafe(next);
    if (next.gameStatus === 'finished') return { ok: true, game: next };
    if (forced) currentPlayer.cards.push(forced);
  }

  if (currentPlayer.cards.length === 0 && card.number !== SPECIAL_CARDS.WHOT.number) {
    next.winner = { id: currentPlayer.id, name: currentPlayer.name };
    next.gameStatus = 'finished';
    next.deckRemaining = next.deck.length;
    return { ok: true, game: next };
  }

  if (card.number === SPECIAL_CARDS.WHOT.number) {
    next.awaitingShapeCallPlayerId = playerId;
    next.deckRemaining = next.deck.length;
    return { ok: true, game: next };
  }

  if (next.holdOn?.active && next.holdOn.holderId === playerId && card.number !== SPECIAL_CARDS.HOLD_ON) {
    next.holdOn = { active: false, holderId: null };
  }

  const nextIdx = applySpecialEffects(next, card, isCounteringPick3);
  if (next.holdOn?.startup && next.holdOn.holderId === playerId) {
    next.holdOn = { active: false, holderId: null };
  } else {
    next.currentPlayerIndex = nextIdx;
  }

  next.deckRemaining = next.deck.length;
  return { ok: true, game: next };
}

function drawCard(game, playerId) {
  if (game.gameStatus !== 'in_progress') return { ok: false, error: 'Game not in progress', game };

  const next = cloneGame(game);
  const currentPlayer = next.players[next.currentPlayerIndex];
  if (!currentPlayer || currentPlayer.id !== playerId) return { ok: false, error: 'Not your turn', game };
  if (next.awaitingShapeCallPlayerId) return { ok: false, error: 'Shape call pending', game };

  const pendingPick3Active = Boolean(next.pendingPick3?.active && next.houseRules.pick3On5);
  const pendingPick2Active = Boolean(next.pendingPick2?.active && next.houseRules.pick2);

  if (pendingPick3Active) {
    const count = next.pendingPick3?.count || 3;
    for (let i = 0; i < count; i += 1) {
      const one = drawOneSafe(next);
      if (next.gameStatus === 'finished') return { ok: true, game: next, meta: { drewCount: i, forced: true } };
      if (one) currentPlayer.cards.push(one);
    }
    next.pendingPick3 = { active: false, count: 3 };
    next.currentPlayerIndex = advancePlayerIndex(next, next.currentPlayerIndex, 1);
    next.deckRemaining = next.deck.length;
    return { ok: true, game: next, meta: { drewCount: count, forced: true } };
  }

  if (pendingPick2Active) {
    const count = next.pendingPick2?.count || 2;
    for (let i = 0; i < count; i += 1) {
      const one = drawOneSafe(next);
      if (next.gameStatus === 'finished') return { ok: true, game: next, meta: { drewCount: i, forced: true } };
      if (one) currentPlayer.cards.push(one);
    }
    next.pendingPick2 = { active: false, count: 2 };
    next.currentPlayerIndex = advancePlayerIndex(next, next.currentPlayerIndex, 1);
    next.deckRemaining = next.deck.length;
    return { ok: true, game: next, meta: { drewCount: count, forced: true } };
  }

  const one = drawOneSafe(next);
  if (next.gameStatus === 'finished') return { ok: true, game: next, meta: { drewCount: 0, forced: false } };
  if (one) currentPlayer.cards.push(one);
  if (next.holdOn?.active && next.holdOn.holderId === playerId) next.holdOn = { active: false, holderId: null };
  next.currentPlayerIndex = advancePlayerIndex(next, next.currentPlayerIndex, 1);
  next.deckRemaining = next.deck.length;

  return { ok: true, game: next, meta: { drewCount: one ? 1 : 0, forced: false } };
}

function callShape(game, playerId, shape) {
  if (game.awaitingShapeCallPlayerId !== playerId) return { ok: false, error: 'Not awaiting shape call', game };

  const validShapes = new Set(Object.values(CARD_SHAPES));
  if (!validShapes.has(shape)) return { ok: false, error: 'Invalid shape', game };

  const next = { ...game, currentShape: shape, awaitingShapeCallPlayerId: null };
  next.currentPlayerIndex = advancePlayerIndex(next, next.currentPlayerIndex, 1);
  next.deckRemaining = next.deck.length;
  return { ok: true, game: next };
}

function runBotTurn(game) {
  if (game.gameStatus !== 'in_progress') return { ok: false, game };
  const currentPlayer = game.players[game.currentPlayerIndex];
  if (!currentPlayer?.isBot) return { ok: false, game };

  if (game.awaitingShapeCallPlayerId === currentPlayer.id) {
    const shape = chooseBestShape(currentPlayer.cards);
    const shapeResult = callShape(game, currentPlayer.id, shape);
    if (!shapeResult.ok) return { ok: false, game };
    return { ok: true, game: shapeResult.game, info: `Bot called ${shape}.` };
  }

  const pendingPick3Active = Boolean(game.pendingPick3?.active && game.houseRules.pick3On5);
  const pendingPick2Active = Boolean(game.pendingPick2?.active && game.houseRules.pick2);
  const top = game.discardPile[game.discardPile.length - 1];

  if (pendingPick3Active) {
    const counter = currentPlayer.cards.find((card) => card.number === SPECIAL_CARDS.PICK_THREE);
    if (counter) {
      const counterPlay = playCard(game, currentPlayer.id, counter.id);
      if (counterPlay.ok) return { ok: true, game: counterPlay.game, info: 'Bot countered Pick 3 with 5.' };
    }
    const drawResult = drawCard(game, currentPlayer.id);
    return drawResult.ok ? { ok: true, game: drawResult.game, info: 'Bot drew 3 cards.' } : { ok: false, game };
  }

  if (pendingPick2Active) {
    const drawResult = drawCard(game, currentPlayer.id);
    return drawResult.ok ? { ok: true, game: drawResult.game, info: 'Bot drew 2 cards.' } : { ok: false, game };
  }

  const playable = currentPlayer.cards.filter((card) => isValidMoveWithShape(card, top, game.currentShape));
  const whotCard = currentPlayer.cards.find((card) => card.number === SPECIAL_CARDS.WHOT.number);

  if (playable.length > 0) {
    const playResult = playCard(game, currentPlayer.id, playable[0].id);
    return playResult.ok
      ? { ok: true, game: playResult.game, info: `Bot played ${playable[0].shape} ${playable[0].number}.` }
      : { ok: false, game };
  }

  if (whotCard) {
    const playWhotResult = playCard(game, currentPlayer.id, whotCard.id);
    return playWhotResult.ok ? { ok: true, game: playWhotResult.game, info: 'Bot played WHOT.' } : { ok: false, game };
  }

  const drawResult = drawCard(game, currentPlayer.id);
  return drawResult.ok ? { ok: true, game: drawResult.game, info: 'Bot went to market.' } : { ok: false, game };
}

export default function WhotAnonymous() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [game, setGame] = useState(() => createNewMatch());
  const [statusMessage, setStatusMessage] = useState('Your turn');

  const players = game.players || [];
  const topCard = game.discardPile[game.discardPile.length - 1] || null;
  const myPlayer = players.find((player) => player.id === PLAYER_ID) || null;
  const botPlayer = players.find((player) => player.id === BOT_ID) || null;
  const currentPlayer = players[game.currentPlayerIndex] || null;

  const isHumanTurn = game.gameStatus === 'in_progress' && currentPlayer?.id === PLAYER_ID;
  const awaitingShapeCall = game.awaitingShapeCallPlayerId === PLAYER_ID;
  const pendingPick2Count = (game.pendingPick2?.active && isHumanTurn) ? (game.pendingPick2?.count || 2) : 0;
  const pendingPick3Active = Boolean(game.pendingPick3?.active && isHumanTurn);

  const pickBannerText = useMemo(() => {
    if (pendingPick2Count > 0) return `Pick ${pendingPick2Count}`;
    if (pendingPick3Active) return 'Pick 3 or counter with a 5';
    return '';
  }, [pendingPick2Count, pendingPick3Active]);

  const resetGame = () => {
    setGame(createNewMatch());
    setStatusMessage('Your turn');
  };

  const onPlayCard = useCallback((card) => {
    if (!card) return;
    const result = playCard(game, PLAYER_ID, card.id);
    if (!result.ok) {
      toast.error(result.error || 'Invalid move');
      return;
    }

    setGame(result.game);
    if (result.game.gameStatus === 'finished') {
      setStatusMessage('You won!');
    } else if (result.game.awaitingShapeCallPlayerId === PLAYER_ID) {
      setStatusMessage('You played WHOT. Choose a shape.');
    } else {
      setStatusMessage('Bot is thinking...');
    }
  }, [game]);

  const onDrawCard = useCallback(() => {
    const result = drawCard(game, PLAYER_ID);
    if (!result.ok) {
      toast.error(result.error || 'Unable to draw');
      return;
    }

    setGame(result.game);
    setStatusMessage(result.meta?.forced ? `You picked ${result.meta.drewCount}. Bot is thinking...` : 'Bot is thinking...');
  }, [game]);

  const onCallShape = useCallback((shape) => {
    const result = callShape(game, PLAYER_ID, shape);
    if (!result.ok) {
      toast.error(result.error || 'Invalid shape');
      return;
    }
    setGame(result.game);
    setStatusMessage(`Shape set to ${shape}. Bot is thinking...`);
  }, [game]);

  useEffect(() => {
    if (game.gameStatus !== 'in_progress') return undefined;
    if (game.awaitingShapeCallPlayerId === PLAYER_ID) return undefined;

    const active = game.players[game.currentPlayerIndex];
    if (!active?.isBot) return undefined;

    const timer = window.setTimeout(() => {
      const result = runBotTurn(game);
      if (!result.ok) return;

      setGame(result.game);
      if (result.game.gameStatus === 'finished') {
        setStatusMessage('Bot won this round.');
      } else {
        const nextCurrent = result.game.players[result.game.currentPlayerIndex];
        setStatusMessage(nextCurrent?.id === PLAYER_ID ? `${result.info || 'Bot moved.'} Your turn.` : (result.info || 'Bot is thinking...'));
      }
    }, BOT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [game]);

  const turnLabel = currentPlayer?.id === PLAYER_ID ? 'You' : (currentPlayer?.name || 'Bot');
  const canPlay = game.gameStatus === 'in_progress';

  return (
    <div className="whot-page whot-page--room whot-page--game">
      <Toaster position="top-right" />
      <div className="whot-shell">
        <div className="whot-header">
          <div>
            <h1 className="whot-title whot-room-title">WHOT Anonymous <small>vs Bot</small></h1>
            <div className="whot-muted" style={{ marginTop: 6 }}>{statusMessage}</div>
            <div className="whot-muted" style={{ marginTop: 4 }}>
              House rules: Pick 2, Pick 3 on 5, Suspension, Hold On, General Market
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="whot-btn" onClick={() => navigate(user ? '/dashboard' : '/')}>Back</button>
            <button className="whot-btn" onClick={() => navigate(user ? '/whot-online' : '/login')}>Online Mode</button>
            <button className="whot-btn whot-btn-primary" onClick={resetGame}>New Game</button>
          </div>
        </div>

        {game.gameStatus === 'finished' ? (
          <div className="whot-card" style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Game Finished!</h2>
            <h3>Winner: {game.winner?.name || '-'}</h3>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="whot-btn whot-btn-primary" onClick={resetGame}>Play Again</button>
              <button className="whot-btn" onClick={() => navigate(user ? '/dashboard' : '/')}>Back</button>
            </div>
          </div>
        ) : (
          <>
            <WhotGameTable
              topRow={(
                <div className="whot-opponents-row">
                  <WhotPlayerHand
                    cards={botPlayer?.cards || []}
                    topCard={topCard}
                    currentShape={game.currentShape}
                    isCurrentPlayer={false}
                    playerName={botPlayer?.name || 'Bot'}
                    isOpponent={true}
                    containerClassName="whot-opponent-panel"
                  />
                </div>
              )}
              topCard={topCard}
              deckRemaining={game.deckRemaining}
              currentShape={game.currentShape}
              turnLabel={turnLabel}
              pickBannerText={pickBannerText}
              onDrawCard={onDrawCard}
              isCurrentPlayer={isHumanTurn}
              awaitingShapeCall={awaitingShapeCall}
              onCallShape={onCallShape}
            >
              <WhotPlayerHand
                cards={myPlayer?.cards || []}
                topCard={topCard}
                currentShape={game.currentShape}
                isCurrentPlayer={isHumanTurn && !awaitingShapeCall}
                pendingPick2Active={Boolean(game.pendingPick2?.active && isHumanTurn)}
                pendingPick3Active={Boolean(game.pendingPick3?.active && isHumanTurn)}
                playerName="You"
                onPlayCard={onPlayCard}
                hiddenCardIds={[]}
                disabled={awaitingShapeCall || !canPlay}
                containerClassName="whot-card--in-table"
              />

              <WhotControls
                onDrawCard={onDrawCard}
                onRestart={resetGame}
                onLeave={() => navigate(user ? '/dashboard' : '/')}
                onStopConfetti={() => {}}
                canDraw={isHumanTurn && canPlay && !awaitingShapeCall}
                gameStatus={game.gameStatus}
                showDrawButton={canPlay}
                showStopConfetti={false}
                pendingPick2Count={pendingPick2Count}
                pendingPick3Active={pendingPick3Active}
              />
            </WhotGameTable>

          </>
        )}

        {!user && (
          <div className="whot-card" style={{ marginTop: 12, textAlign: 'center', padding: '12px 16px' }}>
            <span className="whot-muted">Want real multiplayer, wagers, and rematch?</span>{' '}
            <button className="whot-btn whot-btn-primary" style={{ marginLeft: 8 }} onClick={() => navigate('/login')}>
              Login for Main WHOT
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
